import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import useProfessionalProfile from "../../../hooks/useProfessionalProfile";
import {
  ADDRESS_TYPE_CLINIC,
  ADDRESS_TYPE_DOMICILE,
  CREATABLE_ADDRESS_TYPES,
  CreatableAddressType,
  serverMessage
} from "../../../services/professionals";
import { DEFAULT_COUNTRY_CODE, countryOptions } from "../../../utils/countries";
import { outlinedFieldSx } from "../../../utils/fieldStyles";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

// Registers an address.
//
// There is no update route for an address — only add and remove — so changing a
// workplace means adding the new one and then removing the old. That order is
// not incidental: a professional always keeps at least one practice address, so
// removing the old one first would be refused.
//
// Only the two active types can be chosen. Domicile has no name of its own and
// the server stores it empty, so the field is hidden for it rather than sent and
// silently dropped.

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export default function AddAddressDialog({ open, onClose, onAdded }: Props) {
  const { t } = useTranslation();
  const { addAddress } = useProfessionalProfile();

  const [typeAddress, setTypeAddress] = useState<CreatableAddressType>(
    ADDRESS_TYPE_CLINIC
  );
  const [name, setName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY_CODE);
  const [professional, setProfessional] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTypeAddress(ADDRESS_TYPE_CLINIC);
    setName("");
    setStreet("");
    setCity("");
    setPostalCode("");
    setCountry(DEFAULT_COUNTRY_CODE);
    setProfessional(true);
    setError(null);
  }, [open]);

  const needsName = typeAddress !== ADDRESS_TYPE_DOMICILE;

  const canSubmit =
    street.trim().length > 0 &&
    city.trim().length > 0 &&
    postalCode.trim().length > 0 &&
    country.length > 0 &&
    (!needsName || name.trim().length > 0) &&
    !saving;

  const onSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await addAddress({
        typeAddress,
        ...(needsName ? { name: name.trim() } : {}),
        street: street.trim(),
        city: city.trim(),
        postalCode: postalCode.trim(),
        country,
        professional
      });
      onAdded();
      onClose();
    } catch (err) {
      setError(serverMessage(err) ?? t("contactLists.addressAddError"));
    } finally {
      setSaving(false);
    }
  }, [
    canSubmit,
    typeAddress,
    needsName,
    name,
    street,
    city,
    postalCode,
    country,
    professional,
    addAddress,
    onAdded,
    onClose,
    t
  ]);

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={t("contactLists.addAddressTitle")}
      maxWidth="sm"
    >
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={needsName ? 5 : 12}>
            <TextField
              select
              fullWidth
              label={t("contactLists.addressType")}
              value={typeAddress}
              onChange={event =>
                setTypeAddress(
                  Number(event.target.value) as CreatableAddressType
                )
              }
              sx={outlinedFieldSx}
            >
              {CREATABLE_ADDRESS_TYPES.map(type => (
                <MenuItem key={type} value={type}>
                  {t(`addressType.${type}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {needsName && (
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label={t("contactLists.addressName")}
                value={name}
                onChange={event => setName(event.target.value)}
                sx={outlinedFieldSx}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("contactLists.addressStreet")}
              value={street}
              onChange={event => setStreet(event.target.value)}
              sx={outlinedFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label={t("contactLists.addressPostalCode")}
              value={postalCode}
              onChange={event => setPostalCode(event.target.value)}
              sx={outlinedFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              label={t("contactLists.addressCity")}
              value={city}
              onChange={event => setCity(event.target.value)}
              sx={outlinedFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label={t("contactLists.addressCountry")}
              value={country}
              onChange={event => setCountry(event.target.value)}
              sx={outlinedFieldSx}
            >
              {countryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={professional}
              onChange={event => setProfessional(event.target.checked)}
            />
          }
          label={
            <Typography
              sx={{
                ...typography.body.m,
                color: semanticColors.text.primary
              }}
            >
              {t("contactLists.addressIsWorkplace")}
            </Typography>
          }
        />
        <Typography
          sx={{
            ...typography.body.s,
            color: semanticColors.text.secondary,
            mb: 2
          }}
        >
          {t("contactLists.addressNote")}
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1}>
          <ActionButton
            label={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <ActionButton
            label={t("contactLists.add")}
            onClick={onSubmit}
            loading={saving}
            disabled={!canSubmit}
          />
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
